import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Server-side pagination control. Stateless — the caller owns `offset` and
 * reacts to `onPageChange`. Shows the "1–20 of 504" range like the Cinch
 * billing tables and disables prev/next at the bounds.
 */
export interface PaginationProps {
  limit: number;
  offset: number;
  count: number;
  onPageChange: (offset: number) => void;
  className?: string;
}

export function Pagination({ limit, offset, count, onPageChange, className }: PaginationProps) {
  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, count);
  const atStart = offset <= 0;
  const atEnd = offset + limit >= count;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-1 text-sm text-muted-foreground",
        className,
      )}
    >
      <span className="tabular-nums">
        {from.toLocaleString()}–{to.toLocaleString()} of {count.toLocaleString()}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={atStart}
          onClick={() => onPageChange(Math.max(0, offset - limit))}
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={atEnd}
          onClick={() => onPageChange(offset + limit)}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
